import { v4 as uuidv4 } from 'uuid';
import type {
  CustomNode,
  NodeStruct,
  RepeatBodySlot,
  RepeatPseudoCycleConfig,
} from '@/types/node-types';
import { ConnectionType, type CustomEdge } from '@/types/edge-types';
import type { ExpressionAst, JsonValue } from './functional-control-flow';
import {
  astToCondition,
  conditionToAst,
  collectFieldSuggestions,
  describeCondition,
  type Condition,
  type FieldPath,
} from './switch-condition-builder';
import {
  defaultRepeatPseudoCycleConfig,
  getRepeatPseudoCycleConfig,
  REPEAT_BACK_EDGE_ROLE,
  REPEAT_CONDITION_ROLE,
  REPEAT_DEFAULT_MAX_ITERATIONS,
  REPEAT_MAX_ITERATIONS_HARD,
  REPEAT_SOURCE_HANDLE,
  repeatFeedbackHandleId,
} from './repeat-control';
import {
  canBeRepeated,
  canJudge,
  catalogStructFor,
  describeLoop,
  edgeTargetSlot,
  isRepeatEdge,
  nodeLabel,
  type RepeatLoop,
} from './repeat-loop';

/**
 * The loop as one sentence with pickers:
 *
 *   Repeat [entry] until [judge] says [condition], at most [N] times,
 *   passing [judge output] back into [entry input].
 *
 * `sentenceFromGraph` reads it off the canvas and the authored
 * `repeat_config`; `configFromSentence` and `planLoopEdges` write it back
 * as exactly the contract the backend compiles: the `repeat_config` param
 * (version 2.0, authored half only), the condition edge into the Repeat
 * Until's Evaluator input and the `repeat` back edge onto the entry's
 * feedback slot.
 */

export const JUDGE_ROOT = 'judge';

export interface LoopSentence {
  entryId: string | null;
  judgeId: string | null;
  /** Condition over the judge's output; paths are relative (no `judge` root). */
  condition: Condition;
  maxIterations: number;
  /** Which judge output goes back into the step, relative to the judge root. */
  feedbackPath: FieldPath;
  /** Where on the step the feedback lands. */
  feedbackSlot: RepeatBodySlot | null;
}

const struct = (node: CustomNode | undefined): NodeStruct | undefined =>
  node?.data?.struct;

export const defaultCondition = (): Condition => ({
  kind: 'rule',
  field: ['is_valid'],
  op: 'eq',
  value: true,
  guardMissing: false,
});

/** The step's feedback slot when the contract declares exactly one. */
export const defaultFeedbackSlot = (
  entry: NodeStruct | undefined,
  catalog?: readonly NodeStruct[],
): RepeatBodySlot | null => {
  const slots =
    catalogStructFor(entry, catalog)?.repeat_body_contract?.feedback_slots ??
    [];
  return slots.length >= 1 ? slots[0] : null;
};

export const feedbackSlotsFor = (
  entry: NodeStruct | undefined,
  catalog?: readonly NodeStruct[],
): RepeatBodySlot[] =>
  catalogStructFor(entry, catalog)?.repeat_body_contract?.feedback_slots ?? [];

/** Read the sentence off the canvas and the carrier's authored config. */
export const sentenceFromGraph = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  carrierId: string,
  catalog?: readonly NodeStruct[],
): { sentence: LoopSentence; loop: RepeatLoop | null } => {
  const loop = describeLoop(nodes, edges, carrierId);
  const config = loop ? getRepeatPseudoCycleConfig(struct(loop.carrier)) : null;
  const entry = loop?.entryId
    ? nodes.find((node) => node.id === loop.entryId)
    : undefined;
  const sentence: LoopSentence = {
    entryId: loop?.entryId ?? null,
    judgeId: loop?.evaluatorId ?? null,
    condition: config
      ? astToCondition(config.exit_condition as ExpressionAst, JUDGE_ROOT)
      : defaultCondition(),
    maxIterations: config?.max_iterations ?? REPEAT_DEFAULT_MAX_ITERATIONS,
    feedbackPath: config
      ? (config.feedback_source_path.slice(1) as FieldPath)
      : ['value'],
    feedbackSlot:
      loop?.feedbackSlot ?? defaultFeedbackSlot(struct(entry), catalog),
  };
  return { sentence, loop };
};

/** The authored `repeat_config` for a sentence; never the compiled half. */
export const configFromSentence = (
  sentence: LoopSentence,
  current: RepeatPseudoCycleConfig | null,
  carrierId: string,
): RepeatPseudoCycleConfig => {
  const base = current ?? defaultRepeatPseudoCycleConfig(carrierId);
  return {
    version: '2.0',
    semantic_id: base.semantic_id || `repeat:${carrierId}`,
    max_iterations: Math.min(
      Math.max(1, Math.round(sentence.maxIterations)),
      REPEAT_MAX_ITERATIONS_HARD,
    ),
    exit_condition: conditionToAst(sentence.condition, JUDGE_ROOT) as Record<
      string,
      JsonValue
    >,
    feedback_source_path: [JUDGE_ROOT, ...sentence.feedbackPath],
    feedback_bindings: [],
    emit_mode: 'last_attempt',
    region: null,
    runtime_limits: null,
  };
};

// --- what the pickers offer --------------------------------------------------

export interface PickerOption {
  id: string;
  label: string;
  kind: string;
}

/** Nodes that can be the repeated step (attempt-eligible, not the carrier). */
export const entryCandidates = (
  nodes: readonly CustomNode[],
  carrierId: string,
  catalog?: readonly NodeStruct[],
): PickerOption[] =>
  nodes
    .filter(
      (node) => node.id !== carrierId && canBeRepeated(struct(node), catalog),
    )
    .map((node) => ({
      id: node.id,
      label: nodeLabel(node),
      kind: struct(node)?.display_name ?? '',
    }));

/**
 * Nodes that can judge: judge-eligible and downstream of the entry (when an
 * entry is chosen), excluding the carrier.
 */
export const judgeCandidates = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  carrierId: string,
  entryId: string | null,
  catalog?: readonly NodeStruct[],
): PickerOption[] => {
  let downstream: Set<string> | null = null;
  if (entryId) {
    downstream = new Set<string>();
    const queue = [entryId];
    while (queue.length) {
      const current = queue.shift()!;
      for (const edge of edges) {
        if (isRepeatEdge(edge)) continue;
        const from = edge.source_node_id || edge.source;
        const to = edge.target_node_id || edge.target;
        if (from === current && to !== carrierId && !downstream.has(to)) {
          downstream.add(to);
          queue.push(to);
        }
      }
    }
  }
  const eligible = nodes.filter(
    (node) => node.id !== carrierId && canJudge(struct(node), catalog),
  );
  const reachable = downstream
    ? eligible.filter((node) => downstream.has(node.id))
    : eligible;
  // Nothing downstream yet (a fresh canvas): offer every judge; picking one
  // wires the step into it.
  const offered = reachable.length > 0 ? reachable : eligible;
  return offered.map((node) => ({
    id: node.id,
    label: nodeLabel(node),
    kind: struct(node)?.display_name ?? '',
  }));
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const unwrap = (value: unknown): unknown =>
  isRecord(value) && value._node_output === true ? value.data : value;

/**
 * Field suggestions for the condition and feedback pickers: the judge's
 * output port names first, then paths found in its last result.
 */
export const judgeOutputSuggestions = (
  judge: CustomNode | undefined,
): string[] => {
  const names =
    struct(judge)
      ?.output_ports?.map((port) => port.name)
      .filter(Boolean) ?? [];
  const result = judge?.data?.result as Record<string, unknown> | undefined;
  const sample = isRecord(result) ? unwrap(result.data) : undefined;
  const fromSample = sample ? collectFieldSuggestions(sample, 2, 20) : [];
  return [...new Set([...names, ...fromSample])];
};

export interface JudgePreset {
  id: string;
  label: string;
  condition: Condition;
  feedbackPath: FieldPath;
}

/** One-click conditions that fit the judge's kind. */
export const presetsForJudge = (
  judge: NodeStruct | undefined,
): JudgePreset[] => {
  if (!judge) return [];
  if (judge.node_id === 'validate_node') {
    return [
      {
        id: 'validate-valid',
        label: 'the check passes (is valid)',
        condition: {
          kind: 'rule',
          field: ['is_valid'],
          op: 'eq',
          value: true,
          guardMissing: false,
        },
        feedbackPath: ['value'],
      },
    ];
  }
  if (judge.node_id === 'classify_decide_node') {
    const labelsParam = judge.params?.find(
      (param) => param.field_id === 'decision_labels',
    );
    const labels = (
      Array.isArray(labelsParam?.value)
        ? labelsParam?.value
        : Array.isArray(labelsParam?.default)
          ? labelsParam?.default
          : []
    ) as unknown[];
    return labels
      .filter((label): label is string => typeof label === 'string')
      .map((label) => ({
        id: `classify-${label}`,
        label: `the decision is "${label}"`,
        condition: {
          kind: 'rule',
          field: ['decision'],
          op: 'eq',
          value: label,
          guardMissing: false,
        },
        feedbackPath: ['result'],
      }));
  }
  if (judge.node_id === 'custom_code_node') {
    return [
      {
        id: 'code-done',
        label: 'the script reports done = true',
        condition: {
          kind: 'rule',
          field: ['done'],
          op: 'eq',
          value: true,
          guardMissing: true,
        },
        feedbackPath: [],
      },
    ];
  }
  return [];
};

export const describeSentence = (
  sentence: LoopSentence,
  nodes: readonly CustomNode[],
): string => {
  const name = (id: string | null) =>
    id ? nodeLabel(nodes.find((node) => node.id === id)) : '…';
  const feedback = sentence.feedbackPath.length
    ? sentence.feedbackPath.join('.')
    : 'its output';
  return `Repeat ${name(sentence.entryId)} until ${name(sentence.judgeId)} says ${describeCondition(
    sentence.condition,
  )}, at most ${sentence.maxIterations} times, passing ${feedback} back into ${
    sentence.feedbackSlot?.target_id ?? '…'
  }.`;
};

// --- edges -------------------------------------------------------------------

export interface LoopEdgePlan {
  add: CustomEdge[];
  removeIds: string[];
}

const findPort = (
  node: CustomNode | undefined,
  preferredNames: string[],
): { id: string; name: string } | null => {
  const ports = struct(node)?.output_ports ?? [];
  for (const name of preferredNames) {
    const port = ports.find((candidate) => candidate.name === name);
    if (port) return { id: port.id, name: port.name };
  }
  return ports[0] ? { id: ports[0].id, name: ports[0].name } : null;
};

const carrierInputPort = (carrier: CustomNode | undefined) =>
  struct(carrier)?.params?.find((param) => param.field_id === 'input')
    ?.bindable_config?.input_port?.id ?? null;

/** Build the condition edge: judge output → Repeat Until Evaluator input. */
export const buildConditionEdge = (
  judge: CustomNode,
  carrier: CustomNode,
  conditionField: FieldPath,
  layoutType: string,
): CustomEdge | null => {
  const port = findPort(judge, [
    String(conditionField[0] ?? ''),
    'is_valid',
    'decision',
    'output',
    'result',
  ]);
  const target = carrierInputPort(carrier);
  if (!port || !target) return null;
  return {
    id: uuidv4(),
    source: judge.id,
    sourceHandle: port.id,
    target: carrier.id,
    targetHandle: target,
    sourceNodeId: struct(judge)?.react_flow_generated_id,
    targetNodeId: struct(carrier)?.react_flow_generated_id,
    source_node_id: judge.id,
    source_port_id: port.id,
    target_node_id: carrier.id,
    target_port_id: target,
    target_param_id: 'input',
    type: layoutType,
    edge_type: ConnectionType.PORT_TO_PARAM,
    animated: false,
    data: { repeat_role: REPEAT_CONDITION_ROLE },
  };
};

/** Build the back edge: Repeat Until "repeat" handle → entry feedback slot. */
export const buildRepeatEdge = (
  carrier: CustomNode,
  entry: CustomNode,
  slot: RepeatBodySlot,
  layoutType: string,
): CustomEdge => ({
  id: uuidv4(),
  source: carrier.id,
  sourceHandle: REPEAT_SOURCE_HANDLE,
  target: entry.id,
  targetHandle: repeatFeedbackHandleId(slot),
  sourceNodeId: struct(carrier)?.react_flow_generated_id,
  targetNodeId: struct(entry)?.react_flow_generated_id,
  source_node_id: carrier.id,
  source_port_id: REPEAT_SOURCE_HANDLE,
  target_node_id: entry.id,
  target_port_id: slot.kind === 'input' ? slot.target_id : null,
  target_param_id: slot.kind === 'param' ? slot.target_id : null,
  type: layoutType,
  edge_type: ConnectionType.REPEAT,
  animated: false,
  data: { repeat_role: REPEAT_BACK_EDGE_ROLE },
});

/**
 * Edges to add and remove so the canvas matches the sentence. Existing
 * edges that already say the same thing are kept.
 */
export const planLoopEdges = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  carrierId: string,
  sentence: LoopSentence,
  layoutType: string,
): LoopEdgePlan => {
  const plan: LoopEdgePlan = { add: [], removeIds: [] };
  const carrier = nodes.find((node) => node.id === carrierId);
  if (!carrier) return plan;
  const loop = describeLoop(nodes, edges, carrierId);
  const judge = sentence.judgeId
    ? nodes.find((node) => node.id === sentence.judgeId)
    : undefined;
  const entry = sentence.entryId
    ? nodes.find((node) => node.id === sentence.entryId)
    : undefined;

  // Condition edge.
  const keepCondition = loop?.conditionEdges.find(
    (edge) => (edge.source_node_id || edge.source) === sentence.judgeId,
  );
  for (const edge of loop?.conditionEdges ?? []) {
    if (edge !== keepCondition) plan.removeIds.push(edge.id);
  }
  if (judge && !keepCondition) {
    const built = buildConditionEdge(
      judge,
      carrier,
      sentence.condition.kind === 'rule' ? sentence.condition.field : [],
      layoutType,
    );
    if (built) plan.add.push(built);
  }

  // The judge must read the step's output. When the judge's subject input
  // has nothing connected yet, wire the step straight into it so a loop can
  // be built from the panel alone.
  if (entry && judge && entry.id !== judge.id) {
    const judgeStruct = catalogStructFor(struct(judge), undefined);
    const subjectSlot = judgeStruct?.repeat_body_contract?.subject_slots?.[0];
    const subjectPort = subjectSlot
      ? subjectSlot.kind === 'param'
        ? struct(judge)?.params?.find(
            (param) => param.field_id === subjectSlot.target_id,
          )?.bindable_config?.input_port?.id
        : struct(judge)?.input_ports?.find(
            (port) => port.name === subjectSlot.target_id,
          )?.id
      : undefined;
    const alreadyFed = edges.some(
      (edge) =>
        !isRepeatEdge(edge) &&
        (edge.target_node_id || edge.target) === judge.id &&
        (subjectPort
          ? edge.targetHandle === subjectPort ||
            edge.target_port_id === subjectPort
          : true),
    );
    const stepOutput = struct(entry)?.output_ports?.[0];
    if (subjectSlot && subjectPort && stepOutput && !alreadyFed) {
      plan.add.push({
        id: uuidv4(),
        source: entry.id,
        sourceHandle: stepOutput.id,
        target: judge.id,
        targetHandle: subjectPort,
        sourceNodeId: struct(entry)?.react_flow_generated_id,
        targetNodeId: struct(judge)?.react_flow_generated_id,
        source_node_id: entry.id,
        source_port_id: stepOutput.id,
        target_node_id: judge.id,
        target_port_id: subjectSlot.kind === 'input' ? subjectPort : null,
        target_param_id:
          subjectSlot.kind === 'param' ? subjectSlot.target_id : null,
        type: layoutType,
        edge_type:
          subjectSlot.kind === 'param'
            ? ConnectionType.PORT_TO_PARAM
            : ConnectionType.PORT_TO_PORT,
        animated: false,
        data: {},
      });
    }
  }

  // Repeat edge.
  const keepRepeat = loop?.repeatEdges.find((edge) => {
    const sameEntry = (edge.target_node_id || edge.target) === sentence.entryId;
    const slot = edgeTargetSlot(edge, entry);
    return (
      sameEntry &&
      sentence.feedbackSlot &&
      slot?.kind === sentence.feedbackSlot.kind &&
      slot?.target_id === sentence.feedbackSlot.target_id
    );
  });
  for (const edge of loop?.repeatEdges ?? []) {
    if (edge !== keepRepeat) plan.removeIds.push(edge.id);
  }
  if (entry && sentence.feedbackSlot && !keepRepeat) {
    plan.add.push(
      buildRepeatEdge(carrier, entry, sentence.feedbackSlot, layoutType),
    );
  }
  return plan;
};

/** Apply an edge plan to an edge list (pure). */
export const applyEdgePlan = (
  edges: readonly CustomEdge[],
  plan: LoopEdgePlan,
): CustomEdge[] => [
  ...edges.filter((edge) => !plan.removeIds.includes(edge.id)),
  ...plan.add,
];
